import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SiteNav from "components/Redesign/SiteNav.js";
import SiteFooter from "components/Redesign/SiteFooter.js";
import SwissMap, { LOCATIONS } from "components/Redesign/SwissMap.js";

import photoDachlissen from "assets/img/standorte-photos/dachlissen.jpg";
import photoMalcantone from "assets/img/standorte-photos/malcantone.jpg";
import photoFalera from "assets/img/standorte-photos/falera.jpg";

const MAP_VIEWBOX = { width: 82.25, height: 52.03 };

const FALERA_MALCANTONE_URL =
  "https://direct.capaz.de/Webdirekt/PublicViewServlet.html?URL=22bff7d4-bb9f-4680-97d2-ddf2e05dfd69cc843b33-d261-417b-9471-25af18a37693#JuDatenPublicView";

const MAP_LABELS = [
  { key: "dachlissen", heightKey: "standorte.Dachlissen height", side: "top" },
  { key: "malcantone", heightKey: "standorte.Malcantone height", side: "right" },
  { key: "falera", heightKey: "standorte.Falera height", side: "right" },
];

const COLUMNS = [
  {
    key: "dachlissen",
    photo: photoDachlissen,
    nameKey: "standorte.Dachlissen name",
    personalityKey: "standorte.Dachlissen personality",
    heightKey: "standorte.Dachlissen height",
    sections: [
      { headingKey: "standorte.Dachlissen terroir heading", bodyKey: "standorte.Dachlissen terroir body" },
      { headingKey: "standorte.Dachlissen charakter heading", bodyKey: "standorte.Dachlissen charakter body" },
      { headingKey: "standorte.Dachlissen kulissen heading", bodyKey: "standorte.Dachlissen kulissen body" },
    ],
    cta: {
      labelKey: "standorte.Dachlissen cta",
      href: "https://direct.capaz.de/Webdirekt/PublicViewServlet.html?URL=d04f3da3-1b7f-4245-84bd-01ca471bb5fafc02c128-9027-4674-ad61-b155f1adbc68",
    },
  },
  {
    key: "falera",
    photo: photoFalera,
    nameKey: "standorte.Falera name",
    personalityKey: "standorte.Falera personality",
    heightKey: "standorte.Falera height",
    sections: [
      { headingKey: "standorte.Falera terroir heading", bodyKey: "standorte.Falera terroir body" },
      { headingKey: "standorte.Falera charakter heading", bodyKey: "standorte.Falera charakter body" },
      { headingKey: "standorte.Falera hoehe heading", bodyKey: "standorte.Falera hoehe body" },
    ],
    cta: {
      labelKey: "standorte.Falera cta",
      href: FALERA_MALCANTONE_URL,
    },
  },
  {
    key: "malcantone",
    photo: photoMalcantone,
    nameKey: "standorte.Malcantone name",
    personalityKey: "standorte.Malcantone personality",
    heightKey: "standorte.Malcantone height",
    sections: [
      { headingKey: "standorte.Malcantone terroir heading", bodyKey: "standorte.Malcantone terroir body" },
      { headingKey: "standorte.Malcantone charakter heading", bodyKey: "standorte.Malcantone charakter body" },
      { headingKey: "standorte.Malcantone funfact heading", bodyKey: "standorte.Malcantone funfact body" },
    ],
    cta: {
      labelKey: "standorte.Malcantone cta",
      href: FALERA_MALCANTONE_URL,
    },
  },
];

function LabeledSwissMap() {
  const { t } = useTranslation();
  return (
    <div className="standorte-map">
      <SwissMap width={320} />
      {MAP_LABELS.map(({ key, heightKey, side }) => {
        const loc = LOCATIONS[key];
        const left = (loc.x / MAP_VIEWBOX.width) * 100;
        const top = (loc.y / MAP_VIEWBOX.height) * 100;
        return (
          <div
            key={key}
            className={"standorte-map-label standorte-map-label-" + side}
            style={{ left: left + "%", top: top + "%" }}
          >
            <span className="standorte-map-label-name">{loc.name}</span>
            <span className="standorte-map-label-height">{t(heightKey)}</span>
          </div>
        );
      })}
    </div>
  );
}

function StandortColumn({ col }) {
  const { t } = useTranslation();
  return (
    <div className="standorte-col">
      <div className="standorte-col-photo">
        <img src={col.photo} alt={t(col.nameKey)} loading="lazy" />
      </div>
      <div className="standorte-col-height">{t(col.heightKey)}</div>
      <h3 className="standorte-col-name">
        {t(col.nameKey)} <em>- {t(col.personalityKey)}</em>
      </h3>
      {col.sections.map(section => (
        <div className="standorte-col-section" key={section.headingKey}>
          <h4>{t(section.headingKey)}</h4>
          <p>{t(section.bodyKey)}</p>
        </div>
      ))}
      {col.cta && (
        <a className="cta-secondary" href={col.cta.href} target="_blank" rel="noopener noreferrer">
          {t(col.cta.labelKey)} <span className="arr">→</span>
        </a>
      )}
    </div>
  );
}

function Standorte() {
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="rd">
      <SiteNav />

      <section className="page-shell standorte-intro">
        <div className="wrap">
          <Link to="/ueber-uns" className="rd-back-link">← {t("ueberuns.Page title")}</Link>
          <div className="standorte-intro-grid">
            <div>
              <div className="rd-section-no">{t("standorte.Page label")}</div>
              <h1 className="page-shell-h1 standorte-h1">
                {t("standorte.Page title")}{" "}
                <span className="page-shell-h1-accent">{t("standorte.Page title accent")}</span>
              </h1>
              <p className="page-shell-lede">{t("standorte.Intro text")}</p>
            </div>
            <LabeledSwissMap />
          </div>
        </div>
      </section>

      <section className="standorte-columns-section">
        <div className="wrap">
          <div className="standorte-columns">
            {COLUMNS.map(col => <StandortColumn key={col.key} col={col} />)}
          </div>
          <div className="rd-shop-cta">
            <Link to="/unser-honig" className="cta-primary">
              {t("home.Hero CTA shop")} <span className="arr">→</span>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default Standorte;
